// Order Details "Readiness Checklist" — the card shell + status vocabulary
// (ticket 86d385wc0, Phase 1).
//
// Each field-group is a single-open accordion card laid out as a strict grid:
//   [ status icon ] [ title + collapsed summary ] [ chevron ]
// The status icon sits in a fixed-width left column so the icons line up
// top-to-bottom across the stack — that vertical column is what makes gaps
// "hard to miss" at a glance (recognition over recall).

import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, Lock, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SectionStatusKind } from './sectionStatus';

// Strict vocabulary: icon + label + color, never color alone.
const STATUS_META: Record<
  SectionStatusKind,
  { Icon: typeof CheckCircle2; label: string; className: string }
> = {
  complete: { Icon: CheckCircle2, label: 'Complete', className: 'text-success' },
  attention: { Icon: AlertCircle, label: 'Needs attention', className: 'text-warning' },
  optional: { Icon: Minus, label: 'Optional', className: 'text-muted-foreground' },
  locked: { Icon: Lock, label: 'Inherited / locked', className: 'text-muted-foreground' },
};

export function StatusIndicator({
  kind,
  className,
}: {
  kind: SectionStatusKind;
  className?: string;
}) {
  const { Icon, label } = STATUS_META[kind];
  return (
    <Icon
      className={cn('h-5 w-5', STATUS_META[kind].className, className)}
      aria-label={label}
      role="img"
    />
  );
}

interface ReadinessCardProps {
  /** Stable id for the section/card header (anchored to by the Phase-2 bar). */
  domId: string;
  title: string;
  kind: SectionStatusKind;
  /** One-line summary shown only while collapsed. */
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

export function ReadinessCard({
  domId,
  title,
  kind,
  summary,
  open,
  onToggle,
  children,
}: ReadinessCardProps) {
  const locked = kind === 'locked';
  return (
    <div
      id={domId}
      className={cn(
        'overflow-hidden rounded-lg border transition-colors',
        // Locked groups read as a muted surface so coordinators trust them and
        // don't fiddle (FO pricing / inherited).
        locked ? 'bg-muted/40' : 'bg-card',
        open && 'ring-1 ring-border',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
          'hover:bg-muted/60',
        )}
      >
        {/* Status column — fixed width so icons align across the stack. */}
        <span className="flex w-5 shrink-0 justify-center">
          <StatusIndicator kind={kind} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{title}</span>
            {locked && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
          </span>
          {!open && summary && (
            <span className="truncate text-xs text-muted-foreground">{summary}</span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <div className="border-t px-4 py-4">{children}</div>}
    </div>
  );
}
